// src/pages/Heatmap.js - FIXED: Listen for localStorage changes across tabs
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '../utils/api';
import { Spinner } from '../components/Shared';

const STAGES = [
  { key: 'stage_1_erp',           label: 'ERP Received',   color: 'bg-gray-200 text-gray-700 hover:bg-gray-300',        dot: 'bg-gray-400'   },
  { key: 'stage_2_stock_removal', label: 'Stock Removal',  color: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',  dot: 'bg-yellow-400' },
  { key: 'stage_3_checking',      label: 'Checking',       color: 'bg-blue-100 text-blue-800 hover:bg-blue-200',        dot: 'bg-blue-500'   },
  { key: 'stage_4_packing',       label: 'Ready for Delivery', color: 'bg-purple-100 text-purple-800 hover:bg-purple-200',  dot: 'bg-purple-500' },
  { key: 'stage_5_dispatched',    label: 'In Transit',     color: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',  dot: 'bg-indigo-500' },
  { key: 'stage_6_delivered',     label: 'Delivered',      color: 'bg-green-100 text-green-800 hover:bg-green-200',     dot: 'bg-green-500'  },
  { key: 'reattempt_pending',     label: 'Reattempt',      color: 'bg-orange-100 text-orange-800 hover:bg-orange-200',  dot: 'bg-orange-500' },
  { key: 'cancelled',             label: 'Cancelled',      color: 'bg-red-100 text-red-800 hover:bg-red-200',           dot: 'bg-red-500'    },
  { key: 'complete',              label: 'Complete',        color: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200', dot: 'bg-emerald-500' },
];

// Legend render order (separate from STAGES, which drives the grid columns).
// The old "ready for delivery" teal split is gone — Checking now
// auto-advances straight to Ready for Delivery, so there's no longer a
// "still packing vs ready" distinction to show; every invoice at this
// stage is always immediately ready.
const LEGEND_ITEMS = [
  { key: 'stage_1_erp',           label: 'ERP Received',   dot: 'bg-gray-400'   },
  { key: 'stage_2_stock_removal', label: 'Stock Removal',  dot: 'bg-yellow-400' },
  { key: 'stage_3_checking',      label: 'Checking',       dot: 'bg-blue-500'   },
  { key: 'stage_4_packing',       label: 'Ready for Delivery', dot: 'bg-purple-500' },
  { key: 'stage_5_dispatched',    label: 'In Transit',     dot: 'bg-indigo-500' },
  { key: 'stage_6_delivered',     label: 'Delivered',      dot: 'bg-green-500'  },
  { key: 'reattempt_pending',     label: 'Reattempt',      dot: 'bg-orange-500' },
  { key: 'cancelled',             label: 'Cancelled',      dot: 'bg-red-500'    },
  { key: 'complete',              label: 'Complete',       dot: 'bg-emerald-500' },
];

function colorFor(inv, stage) {
  return stage.color;
}

export default function Heatmap() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(true);        // initial load only
  const [refreshing, setRefreshing] = useState(false);   // subsequent loads (date change, poll, manual refresh)
  const [filter,   setFilter]   = useState('');
  // toISOString() is always UTC — adjust by browser offset first so
  // "today" always means the actual local calendar date.
  const today = (() => {
    const d = new Date();
    const offsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - offsetMs).toISOString().slice(0,10);
  })();
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo,   setDateTo]   = useState(today);
  const firstLoadDone = React.useRef(false);

  const load = useCallback(async () => {
    if (firstLoadDone.current) setRefreshing(true);
    try {
      const data = await get(`/api/invoices?limit=1000&offset=0&sort=mng_number&order=desc&date_from=${dateFrom}&date_to=${dateTo}`);
      setInvoices(data.invoices || []);
    } catch (err) { console.error(err); }
    finally {
      setLoading(false);
      setRefreshing(false);
      firstLoadDone.current = true;
    }
  }, [dateFrom, dateTo]);
  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);
  useEffect(() => {
    const handleSettingsChanged = () => { load(); };
    const handleStorageChange = (e) => {
      if (e.key === 'dms-settings-changed') { handleSettingsChanged(); }
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('dms-settings-changed', handleSettingsChanged);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('dms-settings-changed', handleSettingsChanged);
    };
  }, [load]);

  const grouped = STAGES.reduce((acc, s) => {
    acc[s.key] = invoices.filter(inv =>
      inv.status === s.key &&
      (!filter || inv.invoice_number.toLowerCase().includes(filter.toLowerCase()) ||
       inv.party_name?.toLowerCase().includes(filter.toLowerCase()))
    );
    return acc;
  }, {});

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Invoice Heatmap</h1>
          <p className="text-sm text-gray-500 mt-1">{refreshing ? 'Refreshing…' : `${invoices.length} total invoices across all stages`}</p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <input
            type="text"
            placeholder="Filter by invoice # or party..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button onClick={load}
            className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-8">
        {STAGES.map(stage => {
          const items = grouped[stage.key] || [];
          return (
            <div key={stage.key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${stage.dot}`} />
                  <span className="font-semibold text-sm text-gray-800">{stage.label}</span>
                </div>
                <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {items.length}
                </span>
              </div>
              <div className="p-3 min-h-16">
                {items.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">No invoices</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {items.map(inv => (
                      <button
                        key={inv.invoice_id}
                        onClick={() => navigate(`/invoices/${inv.invoice_id}`)}
                        className={`px-2 py-1 rounded-lg text-xs font-mono font-semibold transition-all ${colorFor(inv, stage)} border border-transparent hover:border-current hover:shadow-sm`}
                        title={`${inv.party_name} — Rs.${inv.invoice_value}`}
                      >
                        {inv.invoice_number}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Colour Legend</h3>
        <div className="flex flex-wrap gap-4">
          {LEGEND_ITEMS.map(s => (
            <div key={s.key} className="flex items-center gap-2">
              <span className={`w-4 h-4 rounded ${s.dot}`} />
              <span className="text-xs text-gray-600">{s.label}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-3">Click any invoice block to open its detail page.</p>
      </div>
    </div>
  );
}
